import { IconArrowRight, IconChevronDown } from "@hawkins/assets";
import cx from "classnames";
import { ResultOf } from "gql.tada";
import { useLenis } from "lenis/react";
import { Dispatch, forwardRef, SetStateAction } from "react";
import { useNavigate } from "react-router";
import kebabCase from "lodash/kebabCase";
import { SolutionsChapterFragment } from "@queries/entries/SolutionsSectionById";
import MobileSubMenu from "@components/shared/MobileSubMenu";

type SolutionsMobileMenuProps = {
  chapters: ResultOf<typeof SolutionsChapterFragment>[];
  activeChapterIndex: number;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
  isExpanded: boolean;
  pageTitle: string;
};

const SolutionsMobileMenu = forwardRef<
  HTMLDivElement,
  SolutionsMobileMenuProps
>(
  (
    { chapters, activeChapterIndex, setIsExpanded, pageTitle, isExpanded },
    forwardedRef
  ) => {
    const navigation = useNavigate();
    const lenis = useLenis();

    const renderChapters = () => {
      return chapters.map(({ menuLabel, eyebrow, _id }, ix) => {
        const chapterTitle = menuLabel ?? eyebrow ?? "";
        const slug = kebabCase(chapterTitle);
        const active = activeChapterIndex === ix;

        return (
          // eslint-disable-next-line react/forbid-elements
          <a
            href={`#${slug}`}
            key={_id}
            data-chapter={slug}
            className={cx(
              "flex justify-between text-body-small py-[14px] rounded-[5px] relative items-center gap-4 text-center px-3",
              active && "bg-palette-current-background text-brand-cream"
            )}
            onClick={(e) => {
              e.preventDefault();
              navigation(`#${slug}`, {
                replace: true,
              });
              setIsExpanded(false);
              const chapterEl = document.getElementById(slug);
              if (chapterEl) {
                lenis?.scrollTo(chapterEl);
              }
            }}
          >
            <span
              className={cx(
                "bg-palette-current-foreground rounded-full w-2 h-2 group-hover:!opacity-100 group-hover/menu:opacity-0",
                active ? "opacity-100" : "opacity-0"
              )}
            />
            <span className="flex-grow">{chapterTitle}</span>
            <IconArrowRight
              className={cx(
                "opacity-0 group-hover:!opacity-100 group-hover/menu:opacity-0",
                active ? "opacity-100" : "opacity-0"
              )}
            />
          </a>
        );
      });
    };

    return (
      <MobileSubMenu
        ref={forwardedRef}
        title={
          <div
            className="flex justify-between items-center"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div
              className={cx(
                "bg-palette-current-foreground rounded-full w-2 h-2",
                isExpanded ? "opacity-0" : "opacity-100"
              )}
            />
            <div className="relative w-full h-[14px] overflow-hidden text-body-regular">
              <p
                className={cx(
                  "absolute inset-0 flex items-center justify-center transition-transform duration-500",
                  isExpanded ? "-translate-y-full -mb-1" : "translate-y-0"
                )}
              >
                {chapters[activeChapterIndex].menuLabel ??
                  chapters[activeChapterIndex].eyebrow ??
                  pageTitle}
              </p>

              <p
                className={cx(
                  "absolute inset-0 flex items-center justify-center transition-transform duration-500",
                  isExpanded ? "translate-y-0" : "translate-y-full"
                )}
              >
                {pageTitle}
              </p>
            </div>
            <IconChevronDown
              className={cx(
                "w-5 h-5 ml-2 flex-shrink-0 transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </div>
        }
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      >
        <div className="flex flex-col pt-8 pb-2 px-2">{renderChapters()}</div>
      </MobileSubMenu>
    );
  }
);

export default SolutionsMobileMenu;
